import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import TasksTimelineFilterActions from 'routes/Dashboard/TasksTimeline/TasksTimelineFilterActions';
import formStyles from '@lib/assets/styles/Form.module.scss';
import useSiteListFilterFields, {
  SITE_LIST_FILTER_EMPTY_VALUES,
} from 'routes/Sites/utils/useSiteListFilterFields';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import SitesFilterFieldNames from '@lib/enums/fieldNames/sitesFilterFieldNames';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import TableIcon from '@lib/assets/icons/view_list_icon.svg?react';
import { SiteListView } from 'routes/Sites/enums';
import { LoadOptions } from '@lib/components/Select/types';
import { Values } from '@lib/interfaces/form';
import styles from './SiteListFilter.module.scss';

type SiteListFilterProps = {
  clearFilter: () => void;
  closeFilter: () => void;
  filterParams: { [key: string]: unknown };
  isFilterModalOpen: boolean;
  onFilterSubmit: (values: Values) => void;
  openFilter: () => void;
  sectorsLoadOptions: LoadOptions;
  setView: (view: SiteListView) => void;
  view: SiteListView;
};

function SiteListFilter({
  clearFilter,
  closeFilter,
  filterParams,
  isFilterModalOpen,
  onFilterSubmit,
  openFilter,
  sectorsLoadOptions,
  setView,
  view,
}: SiteListFilterProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);

  const filterFields = useSiteListFilterFields({
    sectorsLoadOptions,
  });

  const defaultFilterValues =
    filterParams && Object.keys(filterParams).length > 0
      ? filterParams
      : SITE_LIST_FILTER_EMPTY_VALUES;

  const filterCounter = _get(
    filterParams,
    [SitesFilterFieldNames.Sectors, 'length'],
    0,
  );

  return (
    <>
      <PageBody className={styles.filterWrap}>
        <WhiteBox
          className={styles.filter}
          verticalPaddings="extraSmall"
          horizontalPaddings="small"
        >
          <div className={styles.filterRow}>
            <div>
              <Chip
                counter={filterCounter}
                label={t('filter')}
                leftIcon="filter_alt"
                leftIconSymbolsOutlined
                onClick={openFilter}
                size={ChipSizes.large}
                type={ChipTypes.chipFilter}
                tooltipForCroppedTextDisabled
              />
            </div>
            {!!filterCounter && (
              <div>
                <Button
                  buttonSize={ButtonSizes.large}
                  buttonText={t('reset')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={clearFilter}
                />
              </div>
            )}
          </div>
          <div className={styles.viewChangeActions}>
            <Tooltip body={t('list-view')}>
              <IconButton
                icon="grid_view"
                onClick={() => setView(SiteListView.Grid)}
                type={
                  view === SiteListView.Grid
                    ? IconButtonTypes.PrimaryFilled
                    : IconButtonTypes.PrimaryOutlined
                }
              />
            </Tooltip>
            <Tooltip body={t('table-view')}>
              <IconButton
                svgIcon={<TableIcon />}
                onClick={() => setView(SiteListView.Table)}
                type={
                  view === SiteListView.Table
                    ? IconButtonTypes.PrimaryFilled
                    : IconButtonTypes.PrimaryOutlined
                }
              />
            </Tooltip>
            <Tooltip body={t('map-view')}>
              <IconButton
                icon="map"
                symbolsOutlined
                onClick={() => setView(SiteListView.Map)}
                type={
                  view === SiteListView.Map
                    ? IconButtonTypes.PrimaryFilled
                    : IconButtonTypes.PrimaryOutlined
                }
              />
            </Tooltip>
          </div>
        </WhiteBox>
      </PageBody>
      <Modal
        isOpen={isFilterModalOpen}
        caption={t('filter')}
        onClose={closeFilter}
      >
        <Form
          formRef={formRef}
          fields={filterFields}
          onSubmit={onFilterSubmit}
          defaultValues={defaultFilterValues}
          actionsComponent={TasksTimelineFilterActions}
          actionsComponentProps={{
            onCancel: closeFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
    </>
  );
}

export default SiteListFilter;
